import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * What has gone to the customer for each invoice on the Service Logs board, so a card
 * can show whether the invoice or reminder was emailed or sent by CleanMyBed WhatsApp.
 *
 * Email: customer_email_logs rows from the invoice and reminder emails. The log does not
 * store the invoice, so the invoice number is read from the subject ("Invoice INV-ZA-1 from
 * CleanMyBed", "Payment Reminder: Invoice INV-ZA-1, ...").
 * WhatsApp: the Chats messages messaging-send writes for an invoice or reminder (template
 * invoice_*), matched on the invoice number in the message, the same rule the sender uses.
 * The old "from your phone" route opened WhatsApp on the device and recorded nothing.
 */

export type SendState = 'sent' | 'opened' | 'failed';

export interface SendRecord {
  at: string;
  kind: 'invoice' | 'reminder';
  state: SendState;
  /** Why it failed, in plain words, when it did. */
  reason?: string;
}

export interface InvoiceSends {
  email?: SendRecord;
  whatsApp?: SendRecord;
}

interface SendHistoryLog {
  status: string;
  customer_id?: string;
  invoice_number?: string;
  service_date: string;
}

const EMAIL_TEMPLATES: Record<string, SendRecord['kind']> = {
  invoice_send: 'invoice',
  invoice_follow_up: 'reminder',
};

const BATCH = 40;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** True when the text names this exact invoice number (INV-ZA-1 does not match INV-ZA-10). */
function mentionsInvoice(text: string | null | undefined, invoiceNumber: string): boolean {
  const escaped = invoiceNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^A-Za-z0-9-])${escaped}([^A-Za-z0-9-]|$)`).test(String(text ?? ''));
}

function emailState(status: string | null, openedAt: string | null, bouncedAt: string | null): SendState {
  if (bouncedAt || status === 'bounced') return 'failed';
  if (openedAt || status === 'opened' || status === 'clicked') return 'opened';
  return 'sent';
}

/**
 * Meta's failure text in plain words, the same wording the Chats note uses (whatsapp-webhook
 * reportFailed), so the card and the chat say the same thing.
 */
function whatsAppReason(error: string | null): string {
  const why = (error ?? '').replace(/\s+/g, ' ').trim();
  if (/undeliverable/i.test(why)) {
    return 'This number cannot get WhatsApp messages (most likely not on WhatsApp). Sending again will not work. Phone them, or email if we have an address.';
  }
  if (/healthy ecosystem/i.test(why)) {
    return 'WhatsApp held this back: this person has had several marketing messages lately. Try again after 24 hours, or call or email them.';
  }
  return why ? `WhatsApp said: ${why}.` : 'WhatsApp gave no reason.';
}

function whatsAppState(status: string | null): SendState {
  if (status === 'failed') return 'failed';
  if (status === 'read') return 'opened';
  return 'sent';
}

/** The latest email and WhatsApp send per invoice number, for the cards in Sent and Overdue. */
export async function loadSendHistory(
  supabase: SupabaseClient,
  logs: SendHistoryLog[],
): Promise<Map<string, InvoiceSends>> {
  const result = new Map<string, InvoiceSends>();
  const open = logs.filter(l => (l.status === 'sent' || l.status === 'overdue') && l.invoice_number);
  if (open.length === 0) return result;

  const customerIds = [...new Set(open.map(l => l.customer_id).filter((id): id is string => !!id))];
  // Invoice numbers are letters, digits and dashes (checked by the sender), so they are safe in a filter.
  const numbers = [...new Set(open.map(l => l.invoice_number!))].filter(n => /^[A-Za-z0-9_-]+$/.test(n));
  // An invoice is never sent before its service, so nothing older than the oldest service matters.
  const since = open.map(l => l.service_date).sort()[0];

  type EmailRow = { customer_id: string; template_name: string; subject: string | null; status: string | null; sent_at: string; opened_at: string | null; bounced_at: string | null };
  type MessageRow = { template_name: string; body: string | null; delivery_status: string | null; error: string | null; created_at: string };

  // Asked for in batches of the cards' own customers and invoice numbers, so the request stays
  // short and only rows for these invoices come back, well under the API's row cap.
  const emailBatches = chunk(customerIds, BATCH).map(ids => supabase
    .from('customer_email_logs')
    .select('customer_id, template_name, subject, status, sent_at, opened_at, bounced_at')
    .in('template_name', Object.keys(EMAIL_TEMPLATES))
    .in('customer_id', ids));
  const messageBatches = chunk(numbers, BATCH).map(batch => supabase
    .from('conversation_messages')
    .select('template_name, body, delivery_status, error, created_at')
    .like('template_name', 'invoice%')
    .gte('created_at', since)
    .or(batch.map(n => `body.ilike.*${n}*`).join(',')));
  const [emailResults, messageResults] = await Promise.all([Promise.all(emailBatches), Promise.all(messageBatches)]);

  const emailRows: EmailRow[] = [];
  for (const r of emailResults) {
    if (r.error) throw r.error;
    emailRows.push(...(r.data as EmailRow[]));
  }
  const messageRows: MessageRow[] = [];
  for (const r of messageResults) {
    if (r.error) throw r.error;
    messageRows.push(...(r.data as MessageRow[]));
  }
  // Newest first, so the first match below is the latest send.
  emailRows.sort((a, b) => b.sent_at.localeCompare(a.sent_at));
  messageRows.sort((a, b) => b.created_at.localeCompare(a.created_at));

  for (const log of open) {
    const number = log.invoice_number!;
    const sends: InvoiceSends = {};
    const email = emailRows.find(e => e.customer_id === log.customer_id && mentionsInvoice(e.subject, number));
    if (email) {
      sends.email = {
        at: email.sent_at,
        kind: EMAIL_TEMPLATES[email.template_name] ?? 'invoice',
        state: emailState(email.status, email.opened_at, email.bounced_at),
        ...(email.bounced_at || email.status === 'bounced'
          ? { reason: 'The email address bounced. Check it with the customer, or send by WhatsApp.' }
          : {}),
      };
    }
    const message = messageRows.find(m => mentionsInvoice(m.body, number));
    if (message) {
      sends.whatsApp = {
        at: message.created_at,
        kind: message.template_name.startsWith('invoice_reminder') ? 'reminder' : 'invoice',
        state: whatsAppState(message.delivery_status),
        ...(message.delivery_status === 'failed' ? { reason: whatsAppReason(message.error) } : {}),
      };
    }
    result.set(number, sends);
  }
  return result;
}
